import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAccessToken, selectSwitcher, selectUserType } from '../../../redux/selectors';
import { getQaOperatorsAssignments, createQaOperatorsAssignment, getQaOperatorsAssignmentDetails, getQaAgents, addAgentToAssignment, removeAgentFromAssignment, updateQaOperatorsAssignment, getQaOperatorsSupport, deleteQaAssignment } from '../../../services/API';
import { 
  Container,
  SecondModalOverlay,
  SecondModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  SearchSection,
  SearchTitle,
  SearchForm,
  SearchInput,
  SearchButton,
  AgentsListSection,
  AgentItem,
  AgentInfo,
  AgentName,
  AgentLcid,
  AddAgentButton,
  LoadingContainer,
  LoadingText
} from './QaAssignment.styled';
import { Notify } from 'notiflix';

const QaAssignment = () => {
  const token = useSelector(selectAccessToken);
  const switcher = useSelector(selectSwitcher);
  const userType = useSelector(selectUserType);
  const [assignments, setAssignments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    supervisor: 0,
    teamlead: 0
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [assignmentDetails, setAssignmentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showAddAgentsModal, setShowAddAgentsModal] = useState(false);
  const [freeAgents, setFreeAgents] = useState([]);
  const [searchLcid, setSearchLcid] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchYearMonth, setSearchYearMonth] = useState('');
  const [addAgentsLoading, setAddAgentsLoading] = useState(false);
  const [supportData, setSupportData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    supervisor: 0,
    teamlead: 0
  });
  const [editLoading, setEditLoading] = useState(false);

  const getUsernameById = (id) => {
    if (!Array.isArray(supportData)) return id;
    const found = supportData.find(u => String(u.id) === String(id));
    return found ? found.username : id;
  };

  const fetchAssignments = async () => {
    if (!token) {
      setError('Токен доступа отсутствует');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getQaOperatorsAssignments(token);
      console.log('Assignments получены:', data);
      setAssignments(data);
    } catch (error) {
      console.error('Ошибка при получении assignments:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, [token]);

  useEffect(() => {
    const run = async () => {
      try {
        const data = await getQaOperatorsSupport(token);
        console.log('Operators support:', data);
        setSupportData(data);
      } catch (e) {
        console.error('Ошибка при получении operators/support:', e);
      }
    };
    if (token) run();
  }, [token]);

  const handleCreateAssignment = async () => {
    if (!token || userType === 'supervisor') {
      Notify.failure('Токен доступа отсутствует или недостаточно прав');
      return;
    }

    setCreateLoading(true);

    try {
      await createQaOperatorsAssignment(token, formData);
      Notify.success('Назначение успешно создано');
      setShowCreateModal(false);
      setFormData({ supervisor: 0, teamlead: 0 });
      fetchAssignments(); // Обновляем список
    } catch (error) {
      console.error('Ошибка при создании назначения:', error);
      Notify.failure(`Ошибка при создании назначения: ${error.message}`);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: parseInt(value) || 0
    }));
  };

  const handleAssignmentClick = async (assignmentId) => {
    if (!token) {
      Notify.failure('Токен доступа отсутствует');
      return;
    }

    setDetailsLoading(true);
    setShowDetailsModal(true);
    setAssignmentDetails(null);

    try {
      const details = await getQaOperatorsAssignmentDetails(token, assignmentId);
      console.log('Детали назначения получены:', details);
      setAssignmentDetails(details);
    } catch (error) {
      console.error('Ошибка при получении деталей назначения:', error);
      Notify.failure(`Ошибка при получении деталей: ${error.message}`);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleAddAgents = () => {
    if (userType === 'supervisor') return;
    setShowAddAgentsModal(true);
    fetchFreeAgents();
  };

  const refreshAssignmentDetails = async () => {
    if (!token || !assignmentDetails?.id) return;
    try {
      console.log('Обновляем детали назначения для ID:', assignmentDetails.id);
      const updated = await getQaOperatorsAssignmentDetails(token, assignmentDetails.id);
      console.log('Обновлённые детали назначения:', updated); // Для отладки
      console.log('Количество агентов в обновлённых данных:', updated?.agents?.length);
      setAssignmentDetails(updated);
    } catch (e) {
      console.error('Не удалось обновить детали назначения:', e);
    }
  };

  const handleCloseAddAgentsModal = async () => {
    // При закрытии модалки добавления — обновляем детали назначения
    await refreshAssignmentDetails();
    setShowAddAgentsModal(false);
    setSearchLcid('');
    setSearchName('');
    setSearchYearMonth('');
  };

  const handleEditAssignment = () => {
    if (userType === 'supervisor') return;
    setEditFormData({
      supervisor: assignmentDetails.supervisor,
      teamlead: assignmentDetails.teamlead
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditFormData({ supervisor: 0, teamlead: 0 });
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: parseInt(value)
    }));
  };

  const handleUpdateAssignment = async () => {
    if (!token || !assignmentDetails || userType === 'supervisor') return;
    
    setEditLoading(true);
    try {
      await updateQaOperatorsAssignment(token, assignmentDetails.id, editFormData);
      Notify.success('Назначение успешно обновлено');
      await refreshAssignmentDetails();
      handleCloseEditModal();
    } catch (error) {
      console.error('Ошибка при обновлении назначения:', error);
      Notify.failure('Ошибка при обновлении назначения');
    } finally {
      setEditLoading(false);
    }
  };

  const fetchFreeAgents = async () => {
    if (!token || userType === 'supervisor') return;

    setAddAgentsLoading(true);
    try {
      const data = await getQaAgents(token, {
        freeAgent: true,
        lcid: searchLcid,
        name: searchName,
        yearMonth: searchYearMonth,
      });
      setFreeAgents(data);
    } catch (error) {
      console.error('Ошибка при загрузке агентов:', error);
      Notify.failure('Ошибка при загрузке агентов');
    } finally {
      setAddAgentsLoading(false);
    }
  };

  const handleSearchAgents = () => {
    if (userType === 'supervisor') return;
    fetchFreeAgents();
  };

  const handleAddAgentToAssignment = async (agentId) => {
    if (!token || !assignmentDetails || userType === 'supervisor') return;

    try {
      console.log('Добавляем агента с ID:', agentId, 'в назначение:', assignmentDetails.id);
      const result = await addAgentToAssignment(token, assignmentDetails.id, agentId);
      console.log('Результат добавления агента:', result);
      Notify.success('Агент успешно добавлен в назначение');

      // Обновляем детали назначения, но НЕ закрываем модалку добавления
      await refreshAssignmentDetails();

      // Оптимистично убираем агента из локального списка, чтобы сразу пропал
      setFreeAgents(prev => Array.isArray(prev) ? prev.filter(a => String(a.id) !== String(agentId)) : prev);
      
      // Дополнительно подтягиваем актуальный список свободных агентов
      fetchFreeAgents();
    } catch (error) {
      console.error('Ошибка при добавлении агента:', error);
      Notify.failure('Ошибка при добавлении агента в назначение');
    }
  };

  const handleDeleteAssignment = async () => {
    if (!token || !assignmentDetails || userType === 'supervisor') {
      Notify.failure('Недостаточно прав для удаления назначения');
      return;
    }

    if (window.confirm('Вы уверены, что хотите удалить это назначение? Это действие нельзя отменить.')) {
      try {
        await deleteQaAssignment(token, assignmentDetails.id);
        Notify.success('Назначение успешно удалено');
        setShowDetailsModal(false);
        setAssignmentDetails(null);
        
        // Принудительно обновляем список назначений
        await fetchAssignments();
        
        // Также обновляем локальное состояние, убирая удаленное назначение
        setAssignments(prev => {
          if (!prev || !Array.isArray(prev)) return prev;
          return prev.filter(assignment => assignment.id !== assignmentDetails.id);
        });
      } catch (error) {
        console.error('Ошибка при удалении назначения:', error);
        Notify.failure('Ошибка при удалении назначения');
      }
    }
  };

  const handleRemoveAgentFromAssignment = async (agentId) => {
    console.log('handleRemoveAgentFromAssignment вызвана с agentId:', agentId);
    console.log('Текущий assignmentDetails:', assignmentDetails);
    
    if (!token || !assignmentDetails || userType === 'supervisor') {
      console.log('Недостаточно данных для удаления агента или недостаточно прав');
      return;
    }
    
    try {
      console.log('Удаляем агента с ID:', agentId, 'из назначения:', assignmentDetails.id);
      const result = await removeAgentFromAssignment(token, assignmentDetails.id, agentId);
      console.log('Результат удаления агента:', result);
      Notify.success('Агент удалён из назначения');
      
      // Оптимистично удаляем из локального состояния, чтобы UI обновился сразу
      setAssignmentDetails(prev => {
        if (!prev || !Array.isArray(prev.agents)) {
          console.log('Предыдущее состояние не содержит агентов:', prev);
          return prev;
        }
        console.log('Текущие агенты до удаления:', prev.agents);
        const updatedAgents = prev.agents.filter(a => String(a.id) !== String(agentId));
        console.log('Агенты после удаления:', updatedAgents);
        return { ...prev, agents: updatedAgents };
      });
      
      // Затем обновляем детали с сервера
      await refreshAssignmentDetails();
      
      // Обновляем список свободных агентов (удалённый агент становится свободным)
      fetchFreeAgents();
    } catch (e) {
      console.error('Ошибка при удалении агента из назначения:', e);
      Notify.failure('Ошибка при удалении агента');
    }
  };

  // Логируем текущее состояние для отладки
  console.log('QaAssignment рендерится. assignmentDetails:', assignmentDetails);
  console.log('Количество агентов в assignmentDetails:', assignmentDetails?.agents?.length);

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Загрузка данных...
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#dc2626',
          backgroundColor: '#fef2f2',
          borderRadius: '8px',
          border: '1px solid #fca5a5'
        }}>
          <strong>Ошибка:</strong> {error}
        </div>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <div style={{ 
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <h2 style={{ 
            margin: 0, 
            color: '#333',
            marginBottom: '16px'
          }}>
            Назначения операторов QA
          </h2>
          {userType !== 'supervisor' && (
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#3b82f6';
              }}
            >
              Создать назначение
            </button>
          )}
        </div>
        
        {assignments && Array.isArray(assignments) && assignments.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
              gap: '16px',
              alignItems: 'stretch',
              width: '100%'
            }}
          >
            {assignments.map((assignment, index) => (
              <div
                key={assignment.id || index}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '14px',
                  padding: '22px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer',
                }}
                onClick={() => handleAssignmentClick(assignment.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 10px 18px rgba(0,0,0,0.10)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Заголовок назначения */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{
                      margin: 0,
                      color: '#111827',
                      fontSize: '18px',
                      fontWeight: 700
                    }}>
                      Назначение #{assignment.id}
                    </h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#eef2ff',
                      color: '#1f2937',
                      padding: '6px 12px',
                      borderRadius: '9999px',
                      fontSize: '13px'
                    }}>
                      <span style={{ fontWeight: 600 }}>Тимлид:</span>
                      <span>{getUsernameById(assignment.teamlead)}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#f3f4f6',
                      color: '#374151',
                      padding: '6px 12px',
                      borderRadius: '9999px',
                      fontSize: '13px'
                    }}>
                      <span style={{ fontWeight: 600 }}>Супервайзер:</span>
                      <span>{getUsernameById(assignment.supervisor)}</span>
                    </div>
                  </div>
                </div>

                {/* Список агентов убран по требованию. Оставляем только верхнюю информацию. */}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#666',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '2px dashed #d1d5db'
          }}>
            <div style={{ fontSize: '18px', marginBottom: '8px' }}>
              📋
            </div>
            <div style={{ fontSize: '16px', fontWeight: '500' }}>
              Назначения не найдены
            </div>
            <div style={{ fontSize: '14px', marginTop: '4px' }}>
              Нет активных назначений операторов
            </div>
          </div>
        )}
      </Container>

      {/* Модальное окно создания назначения */}
      {showCreateModal && userType !== 'supervisor' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            minWidth: '400px',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '20px' }}>
                Создать назначение
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Супервайзер:
              </label>
              <select
                value={formData.supervisor}
                onChange={(e) => handleInputChange('supervisor', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value={0}>Выберите супервайзера</option>
                {Array.isArray(supportData) && supportData
                  .filter(user => String(user.type).toLowerCase() === 'supervisor')
                  .map(user => (
                    <option key={user.id} value={user.id}>{user.username}</option>
                  ))}
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Тимлид:
              </label>
              <select
                value={formData.teamlead}
                onChange={(e) => handleInputChange('teamlead', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value={0}>Выберите тимлида</option>
                {Array.isArray(supportData) && supportData
                  .filter(user => String(user.type).toLowerCase() === 'tl')
                  .map(user => (
                    <option key={user.id} value={user.id}>{user.username}</option>
                  ))}
              </select>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={createLoading}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleCreateAssignment}
                disabled={createLoading || formData.supervisor === 0 || formData.teamlead === 0}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  background: createLoading || formData.supervisor === 0 || formData.teamlead === 0 ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  cursor: createLoading || formData.supervisor === 0 || formData.teamlead === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {createLoading ? 'Создание...' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно деталей назначения */}
      {showDetailsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            minWidth: '500px',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '20px' }}>
                Детали назначения
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>

            {detailsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                Загрузка деталей...
              </div>
            ) : assignmentDetails ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Заголовок назначения */}
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <h4 style={{
                      margin: 0,
                      color: '#1e293b',
                      fontSize: '18px',
                      fontWeight: '600'
                    }}>
                      Назначение #{assignmentDetails.id}
                    </h4>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      {userType !== 'supervisor' && (
                        <>
                          <button
                            onClick={handleEditAssignment}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '8px',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#f1f5f9';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'none';
                            }}
                            title="Редактировать назначение"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={handleDeleteAssignment}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '8px',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#fef2f2';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'none';
                            }}
                            title="Удалить назначение"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                      <span style={{
                        background: '#3b82f6',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Активное
                      </span>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px'
                  }}>
                    <div style={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        color: '#64748b',
                        marginBottom: '4px'
                      }}>
                        Супервайзер
                      </div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#1e293b'
                      }}>
                        {getUsernameById(assignmentDetails.supervisor)}
                      </div>
                    </div>
                    
                    <div style={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        color: '#64748b',
                        marginBottom: '4px'
                      }}>
                        Тимлид
                      </div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#1e293b'
                      }}>
                        {getUsernameById(assignmentDetails.teamlead)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Список агентов */}
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <h5 style={{
                      margin: 0,
                      color: '#1e293b',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}>
                      Агенты ({assignmentDetails.agents?.length || 0})
                    </h5>
                    {userType !== 'supervisor' && (
                      <button
                        onClick={handleAddAgents}
                        style={{
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#059669';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#10b981';
                        }}
                      >
                        + Добавить агента
                      </button>
                    )}
                  </div>
                  
                  {assignmentDetails.agents && Array.isArray(assignmentDetails.agents) && assignmentDetails.agents.length > 0 ? (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '16px'
                    }}>
                      {assignmentDetails.agents.map((agent, index) => {
                        console.log('Рендерим агента:', agent, 'с индексом:', index);
                        return (
                        <div
                          key={`${agent.id || agent.lcid || index}`}
                          style={{
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '20px',
                            transition: 'box-shadow 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                                                     <div style={{
                             display: 'flex',
                             justifyContent: 'space-between',
                             alignItems: 'flex-start'
                           }}>
                            <div>
                              <div style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#1e293b',
                                marginBottom: '4px'
                              }}>
                                {agent.name}
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: '#64748b',
                                wordBreak: 'break-all'
                              }}>
                                {agent.lcid}
                              </div>
                            </div>
                            <span style={{
                              background: agent.type === 'agent' ? '#dbeafe' : '#fef3c7',
                              color: agent.type === 'agent' ? '#1e40af' : '#92400e',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '500',
                              textTransform: 'uppercase'
                            }}>
                              {agent.type}
                            </span>
                            {userType !== 'supervisor' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Кнопка удаления нажата для агента:', agent);
                                  console.log('agent.id:', agent.id);
                                  console.log('agent.lcid:', agent.lcid);
                                  console.log('agent.name:', agent.name);
                                  console.log('Полная структура агента:', JSON.stringify(agent, null, 2));
                                  handleRemoveAgentFromAssignment(agent.id);
                                }}
                                style={{
                                  marginLeft: '8px',
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '4px 10px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                Удалить
                              </button>
                            )}
                                                     </div>
                         </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      background: '#f8fafc',
                      border: '2px dashed #cbd5e1',
                      borderRadius: '12px',
                      color: '#64748b'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                        👥
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>
                        Агенты не назначены
                      </div>
                      <div style={{ fontSize: '14px' }}>
                        В этом назначении пока нет агентов
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                color: '#dc2626',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #fca5a5'
              }}>
                <strong>Ошибка:</strong> Не удалось загрузить детали назначения
              </div>
            )}
          </div>
        </div>
      )}

            {/* Модальное окно добавления агентов */}
      {showAddAgentsModal && userType !== 'supervisor' && (
        <SecondModalOverlay onClick={handleCloseAddAgentsModal}>
          <SecondModalContent switcher={switcher} onClick={(e) => e.stopPropagation()}>
            <ModalHeader switcher={switcher}>
              <ModalTitle switcher={switcher}>Добавить агентов в назначение</ModalTitle>
              <CloseButton switcher={switcher} onClick={handleCloseAddAgentsModal}>
                ✕
              </CloseButton>
            </ModalHeader>
            
            <SearchSection switcher={switcher}>
              <SearchTitle switcher={switcher}>Поиск свободных агентов</SearchTitle>
              <SearchForm>
                <SearchInput
                  type="text"
                  placeholder="LCID"
                  value={searchLcid}
                  onChange={(e) => setSearchLcid(e.target.value)}
                  switcher={switcher}
                />
                <SearchInput
                  type="text"
                  placeholder="Имя агента"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  switcher={switcher}
                />
                <SearchInput
                  type="text"
                  placeholder="Год-месяц (YYYY-MM)"
                  value={searchYearMonth}
                  onChange={(e) => setSearchYearMonth(e.target.value)}
                  switcher={switcher}
                />
                <SearchButton onClick={handleSearchAgents} disabled={addAgentsLoading}>
                  Поиск
                </SearchButton>
              </SearchForm>
            </SearchSection>

            <AgentsListSection switcher={switcher}>
              {addAgentsLoading ? (
                <LoadingContainer>
                  <LoadingText switcher={switcher}>Загрузка агентов...</LoadingText>
                </LoadingContainer>
              ) : freeAgents && Array.isArray(freeAgents) && freeAgents.length > 0 ? (
                freeAgents.map((agent) => (
                  <AgentItem key={agent.id} switcher={switcher}>
                    <AgentInfo>
                      <AgentName switcher={switcher}>{agent.name}</AgentName>
                      <AgentLcid switcher={switcher}>{agent.lcid}</AgentLcid>
                    </AgentInfo>
                    <AddAgentButton 
                      onClick={() => handleAddAgentToAssignment(agent.id)}
                      disabled={!agent.free_agent}
                      style={{
                        background: agent.free_agent ? '#10b981' : '#9ca3af',
                        cursor: agent.free_agent ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {agent.free_agent ? 'Добавить' : 'Занят'}
                    </AddAgentButton>
                  </AgentItem>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: switcher ? '#9ca3af' : '#6b7280' }}>
                  Агенты не найдены
                </div>
              )}
            </AgentsListSection>
          </SecondModalContent>
        </SecondModalOverlay>
      )}

              {/* Модальное окно редактирования назначения */}
        {showEditModal && userType !== 'supervisor' && (
        <SecondModalOverlay onClick={handleCloseEditModal}>
          <SecondModalContent switcher={switcher} onClick={(e) => e.stopPropagation()}>
            <ModalHeader switcher={switcher}>
              <ModalTitle switcher={switcher}>Редактировать назначение #{assignmentDetails?.id}</ModalTitle>
              <CloseButton switcher={switcher} onClick={handleCloseEditModal}>
                ✕
              </CloseButton>
            </ModalHeader>
            
            <div style={{ padding: '24px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
                marginBottom: '24px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    color: switcher ? '#9ca3af' : '#6b7280',
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>
                    Супервайзер
                  </label>
                  <select
                    value={editFormData.supervisor}
                    onChange={(e) => handleEditInputChange('supervisor', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${switcher ? '#374151' : '#d1d5db'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: switcher ? '#1f2937' : 'white',
                      color: switcher ? '#f9fafb' : '#1f293b'
                    }}
                  >
                    <option value={0}>Выберите супервайзера</option>
                    {supportData && Array.isArray(supportData) && supportData
                      .filter(user => user.type === 'supervisor')
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.username}
                        </option>
                      ))
                    }
                  </select>
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    color: switcher ? '#9ca3af' : '#6b7280',
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>
                    Тимлид
                  </label>
                  <select
                    value={editFormData.teamlead}
                    onChange={(e) => handleEditInputChange('teamlead', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${switcher ? '#374151' : '#d1d5db'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: switcher ? '#1f2937' : 'white',
                      color: switcher ? '#f9fafb' : '#1f293b'
                    }}
                  >
                    <option value={0}>Выберите тимлида</option>
                    {supportData && Array.isArray(supportData) && supportData
                      .filter(user => user.type === 'tl')
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.username}
                        </option>
                      ))
                    }
                  </select>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <button
                  onClick={handleCloseEditModal}
                  style={{
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#4b5563';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#6b7280';
                  }}
                >
                  Отмена
                </button>
                <button
                  onClick={handleUpdateAssignment}
                  disabled={editLoading || editFormData.supervisor === 0 || editFormData.teamlead === 0}
                  style={{
                    background: editFormData.supervisor === 0 || editFormData.teamlead === 0 ? '#9ca3af' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: editFormData.supervisor === 0 || editFormData.teamlead === 0 ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (editFormData.supervisor !== 0 && editFormData.teamlead !== 0) {
                      e.target.style.background = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (editFormData.supervisor !== 0 && editFormData.teamlead !== 0) {
                      e.target.style.background = '#3b82f6';
                    }
                  }}
                >
                  {editLoading ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </SecondModalContent>
        </SecondModalOverlay>
      )}
    </>
  );
};

export default QaAssignment;
